import _ from 'lodash'
import React, { useCallback } from 'react'

export const useMatchCompare = ({ matches, teams }) => {

  const getOpponentKeys = useCallback(({ teamKey }) => {
    return get_opponentKeys(matches, teamKey)
  }, [])

  const getCommonOpponentKeys = useCallback(({ teamAKey, teamBKey }) => {
    return get_commonOpponentKeys(matches, teamAKey, teamBKey)
  }, [])

  const getCommonOpponentMatches = useCallback(({ teamKey }) => {
    const oppKeys = get_opponentKeys(matches, teamKey)
    const oppsM = {}
    _.forEach(oppKeys, oppKey => {
      oppsM[oppKey] = {
        name: teams?.[oppKey]?.name,
        matches: get_commonOpponentMatchesGrouped(matches, teamKey, oppKey)
      }
    })
    return oppsM
  }, [])

  const getOpponentTeams = useCallback(({ opponentKeys }) => {
    return _.filter(teams, t =>
      _.includes(opponentKeys, t._itemKey)
    );
  }, [])

  const getFilteredOpponentsWithMatches = useCallback(({ teamKey, excludeSectionMatches, tpgk }) => {
    const oppsWithMatches = get_filteredOpponentMatches(teams, matches, teamKey, excludeSectionMatches, tpgk)
    return oppsWithMatches
  }, [])

  return { getOpponentKeys, getCommonOpponentKeys, getCommonOpponentMatches, getOpponentTeams, getFilteredOpponentsWithMatches }

}

const matchResult = (match, oppKey, teams, same_section) => {
  const { _results, startDate, _itemKey, teams: teams_match } = match ?? {}
  const { scoreDisplays, winner, loser, complete } = _results ?? {}
  const team_opp = _.without(teams_match, oppKey)
  const team_oppp = teams?.[team_opp[0]]
  const _match = {
    _itemKey,
    complete,
    startDate: startDate,
    sectionMatch: same_section,
    opponentKey: team_oppp?._itemKey,
  }
  if (!complete) return _match
  if (winner?.id === oppKey) {
    return { ..._match, win: true, score: scoreDisplays?.normal, scoreDisplays }
  } else if (loser?.id === oppKey) {
    return { ..._match, win: false, score: scoreDisplays?.normal, scoreDisplays }
  }
}

const get_opponentKeys = (matches, teamKey) =>
  _
    .chain(matches)
    .map(m => {
      const home = _.get(m, 'home.id');
      const away = _.get(m, 'away.id');
      if (home === teamKey) return away;
      if (away === teamKey) return home;
      return null;
    })
    .compact()
    .uniq()
    .value();

const get_commonOpponentKeys = (matches, teamAKey, teamBKey) => {
  const oppA = get_opponentKeys(matches, teamAKey);
  const oppB = get_opponentKeys(matches, teamBKey);
  return _.intersection(oppA, oppB);
};

const get_commonOpponentMatchesGrouped = (matches, teamAKey, teamBKey) => {

  const commonOpponents = get_commonOpponentKeys(matches, teamAKey, teamBKey);

  return _
    .chain(matches)
    .filter(m => {
      const home = _.get(m, 'home.id');
      const away = _.get(m, 'away.id');

      return (
        commonOpponents.includes(home) ||
        commonOpponents.includes(away)
      );
    })
    .reduce((acc, m) => {
      const home = _.get(m, 'home.id');
      const away = _.get(m, 'away.id');

      if (commonOpponents.includes(home)) {
        acc[home] = acc[home] || [];
        acc[home].push(m);
      }

      if (commonOpponents.includes(away)) {
        acc[away] = acc[away] || [];
        acc[away].push(m);
      }

      return acc;
    }, {})
    .value();
};

const get_filteredOpponentMatches = (teams, matches, selectedTeamKey, excludeSectionMatches, tpgk) => {

  const selectedTeam = teams?.[selectedTeamKey];
  if (!selectedTeam) return {};

  const selectedOpponents = get_opponentKeys(matches, selectedTeamKey);
  const selectedOppSet = new Set(selectedOpponents);

  return _
    .chain(selectedOpponents)
    .map(opponentKey => {

      const opponentTeam = teams?.[opponentKey];
      if (!opponentTeam) return null;

      const same_section = sameSection(selectedTeam, opponentTeam);
      if (excludeSectionMatches && same_section) return null;

      if (tpgk) {
        if (!tpgk.includes(opponentKey)) return null;
      }

      // -------------------------
      // 1. Direct matches (H2H)
      // -------------------------
      const matches_direct = _
        .chain(matches)
        .filter(m => {
          const home = _.get(m, 'home.id');
          const away = _.get(m, 'away.id');

          return (
            (home === selectedTeamKey && away === opponentKey) ||
            (away === selectedTeamKey && home === opponentKey)
          );
        })
        .map(m => matchResult(m, opponentKey, teams, same_section))
        .value();

      // -------------------------
      // 2. Common opponents
      // -------------------------
      const opponentOpps = get_opponentKeys(matches, opponentKey);

      const commonOppKeys = opponentOpps.filter(k =>
        k !== selectedTeamKey &&
        k !== opponentKey &&
        selectedOppSet.has(k)
      );

      const commonOpponents = _
        .chain(commonOppKeys)
        .map(commonKey => {

          // opponent vs common opponent
          const oppMatches = _
            .chain(matches)
            .filter(m => {
              const home = _.get(m, 'home.id');
              const away = _.get(m, 'away.id');

              return (
                (home === opponentKey && away === commonKey) ||
                (away === opponentKey && home === commonKey)
              );
            })
            .map(m => matchResult(m, opponentKey, teams, same_section))
            .value();

          // selected team vs same common opponent
          const selMatches = _
            .chain(matches)
            .filter(m => {
              const home = _.get(m, 'home.id');
              const away = _.get(m, 'away.id');

              return (
                (home === selectedTeamKey && away === commonKey) ||
                (away === selectedTeamKey && home === commonKey)
              );
            })
            .map(m => matchResult(m, selectedTeamKey, teams, same_section))
            .value();

          if (!oppMatches.length && !selMatches.length) return null;

          return {
            opponentKey: commonKey,
            name: _.get(teams, [commonKey, 'name']),
            matches_opp: oppMatches,
            matches_vs: selMatches
          };
        })
        .compact()
        .keyBy('opponentKey')
        .mapValues(v => ({
          name: v.name,
          matches_opp: v.matches_opp,
          matches_vs: v.matches_vs,
          _itemKey: v.opponentKey,
        }))
        .value();

      // -------------------------
      // 3. Final filter
      // -------------------------
      if (!matches_direct.length && _.isEmpty(commonOpponents)) return null;

      return {
        opponentKey,
        name: _.get(teams, [opponentKey, 'name']),
        matches_direct,
        commonOpponents
      };
    })
    .compact()
    .keyBy('opponentKey')
    .mapValues(v => ({
      name: v.name,
      matches_direct: v.matches_direct,
      commonOpponents: v.commonOpponents,
      _itemKey: v.opponentKey,
    }))
    .value();
};

const sameSection = (team, oppTeam) => {
  return (team?.levels === oppTeam?.levels) &&
    (team?.sections === oppTeam?.sections)
}

const getOpponentKeys = (matches, teamKey) =>
  _
    .chain(matches)
    .map(m => {
      const home = _.get(m, 'home.id');
      const away = _.get(m, 'away.id');
      if (home === teamKey) return away;
      if (away === teamKey) return home;
      return null;
    })
    .compact()
    .uniq()
    .value();

const getCommonOpponents = (matches, team1Key, team2Key) => {
  const opps1 = getOpponentKeys(matches, team1Key);
  const opps2 = getOpponentKeys(matches, team2Key);

  return _.intersection(opps1, opps2);
};
